import React, { useEffect, useState } from 'react';
import { Button, Input, ToastMessage } from '@cokitchen/cokitchen-components';
import { DeleteIcon } from 'cokitchen-icons';
import { toast } from 'react-toastify';
import { apiService } from 'services/base.service';
import { handleApiError } from 'utils/functions';
import { PrepRequestType } from 'models/prep-request.model';
import BatchSelectDropdown from 'pages/prep-team/kitchen-request/update-kitchen-request/batch-select-dropdown';

export type BatchType = {
  batch_no: number;
  quantity: string;
};

export type SendItemFormData = {
  prep_ingredient_id: string;
  prep_request_item_id: string;
  batch: BatchType[];
};

type Props = {
  requestId: string;
  selectedItemId: string;
  storeId: string;
  apiEndpoint: string;
  successMessage?: string;
  onSuccess?: () => void;
  onCancel?: () => void;
};

const initialBatch: BatchType = {
  batch_no: 0,
  quantity: ''
};

const BatchItemsForm: React.FC<Props> = ({
  requestId,
  selectedItemId,
  storeId,
  apiEndpoint,
  successMessage = 'Items submitted successfully',
  onSuccess,
  onCancel
}) => {
  const [loading, setLoading] = useState(false);
  const [itemRequestToUpdate, setItemRequestToUpdate] = useState<PrepRequestType>(
    {} as PrepRequestType
  );
  const [sendItemFormData, setSendItemFormData] = useState<SendItemFormData[]>([]);
  const [batchQuantities, setBatchQuantities] = useState<Record<string, string>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [batchSizes, setBatchSizes] = useState<Record<number, number>>({});

  const prepIngredients =
    itemRequestToUpdate?.items?.find((item) => item.id === selectedItemId)?.prep_item_ingredients ||
    [];

  const getRequestItems = async () => {
    if (!requestId) return;
    setLoading(true);
    try {
      const res = await apiService.get(`${apiEndpoint}/${requestId}`, {
        version: 'v2'
      });
      setItemRequestToUpdate(res.data.data);
    } catch (error) {
      handleApiError(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedItemId && itemRequestToUpdate?.items) {
      const selectedItem = itemRequestToUpdate.items.find((item) => item.id === selectedItemId);

      if (selectedItem?.prep_item_ingredients) {
        const initialFormData: SendItemFormData[] = selectedItem.prep_item_ingredients.map(
          (prepIngredient) => ({
            prep_ingredient_id: prepIngredient.id,
            prep_request_item_id: selectedItem.id,
            batch: [{ ...initialBatch }]
          })
        );

        setSendItemFormData(initialFormData);
      }
    }
  }, [selectedItemId, itemRequestToUpdate]);

  useEffect(() => {
    getRequestItems();
  }, [requestId]);

  const setBatchValue = (
    ingredientIndex: number,
    batchIndex: number,
    field: keyof BatchType,
    value: string | number
  ) => {
    setSendItemFormData((prev) =>
      prev.map((ingredient, iIdx) =>
        iIdx === ingredientIndex
          ? {
              ...ingredient,
              batch: ingredient.batch.map((b, bIdx) =>
                bIdx === batchIndex ? { ...b, [field]: value } : b
              )
            }
          : ingredient
      )
    );
  };

  const addBatch = (ingredientIndex: number) => {
    setSendItemFormData((prev) =>
      prev.map((ingredient, iIdx) =>
        iIdx === ingredientIndex
          ? {
              ...ingredient,
              batch: [...ingredient.batch, { ...initialBatch }]
            }
          : ingredient
      )
    );
  };

  const removeBatch = (ingredientIndex: number, batchIndex: number) => {
    setSendItemFormData((prev) =>
      prev.map((ingredient, iIdx) =>
        iIdx === ingredientIndex
          ? {
              ...ingredient,
              batch: ingredient.batch.filter((_, bIdx) => bIdx !== batchIndex)
            }
          : ingredient
      )
    );

    setErrors((prev) => {
      const newErrors = { ...prev };
      const key = `${ingredientIndex}-${batchIndex}`;
      delete newErrors[key];
      return newErrors;
    });
  };

  const handleQuantityChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    ingredientIndex: number,
    batchIndex: number
  ) => {
    const inputValue = e.target.value;
    const key = `${ingredientIndex}-${batchIndex}`;
    const maxQuantity = batchQuantities[key];
    const val = Number(inputValue);

    if (maxQuantity && val > Number(maxQuantity)) {
      setErrors((prev) => ({
        ...prev,
        [key]: `Quantity sent cannot be more than the value of the ${maxQuantity}.`
      }));
      return;
    }

    const currentTotal = sendItemFormData[ingredientIndex].batch.reduce(
      (acc, curr, idx) => (idx === batchIndex ? acc : acc + Number(curr.quantity)),
      0
    );

    const prepIngredient = prepIngredients[ingredientIndex];

    if (
      currentTotal + val + (prepIngredient?.quantity_sent || 0) >
      Number(prepIngredient?.quantity_requested || 0)
    ) {
      setErrors((prev) => ({
        ...prev,
        [key]: 'Total quantity sent cannot exceed requested quantity.'
      }));
      return;
    }

    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[key];
      return newErrors;
    });

    setBatchValue(ingredientIndex, batchIndex, 'quantity', inputValue);
  };

  const handleSubmit = async () => {
    if (!storeId || !selectedItemId) return;

    const selectedItem = itemRequestToUpdate.items?.find((item) => item.id === selectedItemId);

    if (!selectedItem) {
      toast(<ToastMessage message='Selected item not found' type='error' />);
      return;
    }

    const data = {
      ingredients: sendItemFormData
        .map((ingredient) => ({
          prep_ingredient_id: ingredient.prep_ingredient_id,
          prep_request_item_id: ingredient.prep_request_item_id,
          batch: ingredient.batch
            .filter((b) => b.batch_no > 0 && b.quantity)
            .map((b) => ({
              batch_no: b.batch_no,
              quantity: Number(b.quantity)
            }))
        }))
        .filter((ingredient) => ingredient.batch.length > 0),
      items: [
        {
          prep_request_item_id: selectedItem.id,
          quantity: Number(selectedItem.quantity_requested)
        }
      ]
    };

    const requestIdForSubmit = itemRequestToUpdate.items?.find(
      (item) => item.id === selectedItemId
    )?.request_id;

    try {
      setLoading(true);
      const res = await apiService.put(`${apiEndpoint}/${requestIdForSubmit}/sent`, {
        version: 'v2',
        data
      });
      if (res) {
        toast(<ToastMessage message={successMessage} type='success' />);
        if (onSuccess) onSuccess();
      }
    } catch (error) {
      handleApiError(error);
    } finally {
      setLoading(false);
    }
  };

  const getSelectedBatchNumbers = (ingredientIndex: number): number[] => {
    return (
      sendItemFormData[ingredientIndex]?.batch
        .map((b) => b.batch_no)
        .filter((batchNo) => batchNo > 0) || []
    );
  };

  if (loading && !itemRequestToUpdate?.items) {
    return (
      <div className='flex justify-center items-center py-8'>
        <p className='text-gray-500'>Loading ingredients...</p>
      </div>
    );
  }

  return (
    <>
      <div className='px-3 md:px-4 py-4'>
        {prepIngredients?.map((prepIngredient, ingredientIndex) => {
          const ingredientFormData = sendItemFormData[ingredientIndex];

          return (
            <div key={prepIngredient.id} className='mb-6 pb-6 border-b border-[#EAEBEB]'>
              <div className='flex flex-col sm:flex-row sm:justify-between sm:items-center mb-4 gap-2'>
                <h2 className='font-bold text-[#2E3031] text-sm md:text-base'>
                  {prepIngredient.ingredient?.name || 'Unknown Ingredient'}
                </h2>
                <p className='text-blue-5 text-xs md:text-sm font-medium'>
                  {prepIngredient.quantity_requested}{' '}
                  {prepIngredient.ingredient?.unit_type || 'units'} requested
                </p>
              </div>

              {ingredientFormData?.batch.map((batch, batchIndex) => {
                const key = `${ingredientIndex}-${batchIndex}`;
                const maxQuantity = batchQuantities[key];

                return (
                  <div
                    key={batchIndex}
                    className={`flex flex-col md:grid md:grid-cols-2 gap-3 md:gap-4 ${
                      batchIndex > 0 ? 'pt-4' : ''
                    } ${
                      batchIndex < ingredientFormData.batch.length - 1
                        ? 'pb-4 border-b border-[#EAEBEB]'
                        : ''
                    }`}>
                    <div className='relative'>
                      <Input
                        design='bordered'
                        label='Quantity sent per batch'
                        name='quantity'
                        placeholder='0'
                        value={batch.quantity}
                        disabled={!batch.batch_no}
                        onChange={(e) => handleQuantityChange(e, ingredientIndex, batchIndex)}
                        inputClass='w-full'
                        error={errors[`${ingredientIndex}-${batchIndex}`]}
                        onBlur={() => setErrors({})}
                      />
                    </div>
                    <div className='flex gap-3 items-end md:items-center'>
                      <div className='flex-1'>
                        <BatchSelectDropdown
                          ingredientId={prepIngredient.ingredient_id}
                          storeId={storeId}
                          batch_no={batch.batch_no}
                          selectedBatchNos={getSelectedBatchNumbers(ingredientIndex)}
                          currentIndex={batchIndex}
                          onBatchSizeChange={(size) =>
                            setBatchSizes((prev) => ({ ...prev, [ingredientIndex]: size }))
                          }
                          onBatchSelect={(batch_no, quantity) => {
                            setBatchValue(ingredientIndex, batchIndex, 'batch_no', batch_no);
                            setBatchValue(ingredientIndex, batchIndex, 'quantity', '');
                            setBatchQuantities((prev) => ({
                              ...prev,
                              [key]: quantity
                            }));
                            setErrors((prev) => {
                              const newErrors = { ...prev };
                              delete newErrors[key];
                              return newErrors;
                            });
                          }}
                        />
                        {maxQuantity && (
                          <p className='text-xs md:text-sm text-[#96999C] mt-2'>
                            <span className='text-[#0080E5]'>Maximum Quantity:</span> {maxQuantity}{' '}
                            {prepIngredient.ingredient?.unit_type || 'units'}
                          </p>
                        )}
                      </div>
                      {ingredientFormData.batch.length > 1 && (
                        <button
                          onClick={() => removeBatch(ingredientIndex, batchIndex)}
                          className='pb-2 md:pb-0'>
                          <DeleteIcon className='text-red-5 cursor-pointer w-5 h-5' />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}

              {batchSizes[ingredientIndex] > 1 && (
                <button
                  onClick={() => addBatch(ingredientIndex)}
                  className='text-blue-5 mt-4 font-medium text-xs md:text-sm'>
                  + Add Batch
                </button>
              )}
            </div>
          );
        })}
      </div>

      <div>
        <hr className='border-t-4 border-[#F7F7F8]' />
        <div className='flex flex-col sm:flex-row gap-3 sm:gap-6 items-stretch sm:items-center justify-end mt-4 sm:mt-6 px-4 sm:mx-[28px] sm:ml-auto'>
          <Button
            variant='block'
            color='gray'
            onClick={() => onCancel && onCancel()}
            disabled={loading}
            className='max-w-[153px]'>
            Cancel
          </Button>
          <Button
            variant='block'
            color='primary'
            onClick={handleSubmit}
            disabled={loading}
            className='max-w-[153px]'>
            {loading ? 'Submitting...' : 'Confirm'}
          </Button>
        </div>
      </div>
    </>
  );
};

export default BatchItemsForm;
